import { type ReactNode } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { EmptyState, Skeleton } from '../../components/ui/states';
import { MyIssuesPage } from '../../features/issues/components/my-issues-page';
import { useLimitedShellCan } from '../../lib/shell-mode';

/**
 * `issue` là uuid — `JSON.parse` không đọc nổi nên đi tới schema dưới dạng chuỗi
 * (bẫy APM-903b chỉ áp cho thứ JSON hoá được). Sai định dạng ⇒ bỏ, không lỗi trang.
 */
const IssuesSearch = z.object({ issue: z.uuid().optional().catch(undefined) });

/**
 * "Issue của tôi" cho nhân viên — APM-ISX-e: issue mình gửi hoặc được chia sẻ ở mọi
 * dự án mình là thành viên. Khách hàng dùng cùng component ở `/portal`.
 */
export const Route = createFileRoute('/_app/issues')({
  validateSearch: IssuesSearch,
  component: MyIssuesRoute,
});

function MyIssuesRoute(): ReactNode {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  /*
    Limited shell (APM-PSPACE-g): a guest's default role has no `issue:read`, and the
    three `me/issues` routes answer 403 to a session without it in any project — say
    so instead of firing them.
  */
  const readable = useLimitedShellCan('issue:read');
  return (
    <div className="flex flex-col gap-4">
      <div>
        <h1 className="text-xl font-medium">{t('myIssues.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('myIssues.subtitleStaff')}</p>
      </div>
      {readable === undefined ? (
        <Skeleton className="h-40 w-full" />
      ) : readable ? (
        <MyIssuesPage
          audience="staff"
          openIssueId={search.issue ?? null}
          onOpenIssue={(issue) => void navigate({ search: issue ? { issue } : {} })}
        />
      ) : (
        <EmptyState
          title={t('myIssues.noAccess.title')}
          description={t('myIssues.noAccess.description')}
        />
      )}
    </div>
  );
}
